'use client';

import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import appCommunicationManager, {
  EAppCommunicationFuncName,
} from '@/utils/app-communication-manager';

interface UseCustomerServiceOptions {
  /**
   * 是否在 App 内嵌环境
   */
  inAppH5?: boolean;
}

interface UseCustomerServiceReturn {
  /**
   * 打开 Instadesk
   */
  openInstadesk: () => void;
  /**
   * 打开 Shulex（iframe 弹窗）
   */
  openShulex: () => void;
  /**
   * 关闭 Shulex iframe 弹窗
   */
  closeShulex: () => void;
  /**
   * 打开客服（根据 deskType 自动选择）
   */
  openCustomerService: () => void;
}

/**
 * 客服服务 Hook
 *
 * 提供打开客服的函数，状态管理通过全局 store 的 actions 操作
 */
export function useCustomerService(
  options: UseCustomerServiceOptions = {},
): UseCustomerServiceReturn {
  const { inAppH5 = false } = options;

  const customerService = useStore((state) => state.customerService);
  const setCustomerService = useStore((state) => state.setCustomerService);

  /**
   * 打开 Instadesk
   */
  const openInstadesk = () => {
    if (inAppH5) {
      appCommunicationManager.sendMessage(EAppCommunicationFuncName.LIVECHAT, { to: 'chat' });
      return;
    }
    setCustomerService({ showInstadesk: true, showShulexIframe: false });
  };

  /**
   * 打开 Shulex iframe 弹窗
   */
  const openShulex = () => {
    if (inAppH5) {
      appCommunicationManager.sendMessage(EAppCommunicationFuncName.LIVECHAT, { to: 'chat' });
      return;
    }
    if (!customerService.shulexIframeUrl) {
      logger.warn('Shulex iframe URL is empty, skip open');
      return;
    }
    setCustomerService({ showShulexIframe: true, showInstadesk: false });
  };

  /**
   * 关闭 Shulex iframe 弹窗
   */
  const closeShulex = () => {
    setCustomerService({ showShulexIframe: false });
  };

  /**
   * 打开客服（根据 deskType 自动选择）
   */
  const openCustomerService = () => {
    if (customerService.deskType === 'instadesk' && !customerService.showInstadesk) {
      openInstadesk();
      return;
    }
    if (customerService.deskType === 'shulex' && !customerService.showShulexIframe) {
      openShulex();
      return;
    }
    // Zendesk
    if (typeof window !== 'undefined' && (window as any).zE) {
      try {
        (window as any).zE('messenger', 'open');
      } catch (error) {
        logger.error('Zendesk error:', error);
      }
    }
  };

  return {
    openInstadesk,
    openShulex,
    closeShulex,
    openCustomerService,
  };
}
